import { createRootRoute, Navigate, Outlet, useRouterState } from '@tanstack/react-router'
import { ThemeProvider } from 'next-themes'
import { Toaster } from '@platform/ui'
import { DemoTourProvider, RecorridoAutomatico } from '@/modules/demo/DemoTour'
import { DemoSwitcher } from '@/modules/demo/DemoSwitcher'
import { leadIdActual } from '@/lib/demo/lead-ref'

/*
 * INSTANCIA COMPARTIDA (rama demo-leads) — qué NO se monta aquí y por qué.
 *
 * `KycRealtimeListener` se cayó del árbol. Abría un socket de Phoenix contra el
 * indexador para escuchar `kyc:verified` / `kyc:rejected`, y esta instancia no
 * tiene indexador: no hay contratos desplegados, no hay eventos de cadena que
 * indexar y no hay Elixir corriendo. El componente ya se autodesactivaba con
 * `features.demoMode`, así que esto no cambia el comportamiento — cambia el
 * peso: sin él, `phoenix` deja de entrar al paquete que se sirve, y nadie que
 * lea este archivo mañana va a suponer que hay un servicio en tiempo real
 * detrás. El módulo sigue en `lib/realtime/` intacto para las otras ramas.
 */

/*
 * Las herramientas de desarrollo del enrutador se quitaron del árbol.
 *
 * Se pintaban abajo a la derecha, que es exactamente donde vive el conmutador
 * flotante entre inversor y emisor. En desarrollo tapaban el botón y el diálogo
 * que abre — o sea, estorbaban justo la pieza que produce la señal de intención
 * más alta del catálogo.
 *
 * En esta rama no compensan: es una instancia de demostración, y lo que hay que
 * poder ver sin obstáculos es el producto, no el árbol de rutas.
 */

export const Route = createRootRoute({
  component: RootLayout,
})

function RootLayout() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
      {/*
        El proveedor del recorrido envuelve todo el árbol porque el conmutador
        flotante ofrece "repetir el recorrido" y necesita `useTour`, y el
        recorrido cruza rutas distintas (catálogo, ficha, cartera).
      */}
      <DemoTourProvider>
        <div className="min-h-screen bg-background text-foreground">
          <PuertaDeAcceso>
            <Outlet />
          </PuertaDeAcceso>
        </div>
        <ConmutadorSiCorresponde />
      </DemoTourProvider>
      <Toaster />
    </ThemeProvider>
  )
}

/**
 * Rutas que se pueden abrir sin haber canjeado un enlace.
 *
 * `/enter` es la puerta, así que obviamente. La raíz explica que se entra por
 * invitación. Y las de recuperación y desbloqueo se dejan abiertas porque son
 * caminos de auxilio: cerrarlas dejaría encerrado justo a quien tiene un
 * problema.
 */
const RUTAS_ABIERTAS = ['/enter', '/recovery', '/unblock']

/**
 * Cierra la instancia a quien no llegó por un enlace personal.
 *
 * POR QUÉ ESTO NO ES SEGURIDAD, Y POR QUÉ IGUAL VA
 *
 * No protege datos: aquí no hay nada que proteger, todo es simulacro y el
 * catálogo es público. Lo que protege es la ATRIBUCIÓN. Quien recorre la
 * plataforma sin enlace no tiene a quién imputarse, así que su recorrido no
 * genera señal y en la bandeja comercial no aparece nadie. Y peor que no medir
 * es medir a medias y creer que el número significa algo.
 *
 * Por eso el corte está en la interfaz y no en el servidor: el servidor ya hace
 * lo suyo —ignora los beacons sin prospecto—, y lo que faltaba era no invitar a
 * recorrer la plataforma por una puerta que no cuenta.
 */
function PuertaDeAcceso({ children }: { children: React.ReactNode }) {
  const ruta = useRouterState({ select: (s) => s.location.pathname })
  const abierta = RUTAS_ABIERTAS.some((r) => ruta === r || ruta.startsWith(`${r}/`))

  // La raíz ya es la pantalla de "se entra por invitación": dejarla pasar evita
  // envolver una explicación dentro de otra.
  if (ruta === '/' || abierta) return <>{children}</>

  if (!leadIdActual()) return <Navigate to="/" replace />

  return <>{children}</>
}

/**
 * El conmutador solo tiene sentido una vez dentro de la plataforma. En la
 * portada, en el canje del enlace o en el registro sería ruido: todavía no hay
 * dos caras entre las que elegir.
 */
function ConmutadorSiCorresponde() {
  const ruta = useRouterState({ select: (s) => s.location.pathname })
  if (!ruta.startsWith('/dashboard')) return null
  return (
    <>
      <RecorridoAutomatico />
      <DemoSwitcher perfilActual={ruta.startsWith('/dashboard/admin') ? 'emisor' : 'inversor'} />
    </>
  )
}
